/**
 * 📦 **받은 그림을 어느 폴더에 넣나** — 자리 이름의 앞머리로 가른다.
 *
 * 200장을 한 폴더에 쏟아 놓으면 받아도 못 쓴다. 운영자가 아는 말(메뉴 이름)로 나눠 담아야
 * 「굿즈 사진만 인쇄소에 보내기」 같은 게 된다.
 *
 * 🔴 **모르는 앞머리도 반드시 담긴다**(기타). 표에 없다고 빠뜨리면 「전부 받기」가 전부가
 * 아니게 되는데, 받은 쪽은 그 사실을 알 길이 없다 — 이 저장소가 거듭 겪은 조용한 누락이다.
 */
export interface AssetGroup {
  /** ZIP 안의 폴더 이름 = 운영자가 아는 메뉴 이름. */
  folder: string;
  /** 이 앞머리로 시작하는 자리들. */
  prefixes: readonly string[];
}

export const ASSET_GROUPS: readonly AssetGroup[] = [
  { folder: "전자책", prefixes: ["book."] },
  { folder: "캐릭터", prefixes: ["hero.base.", "npc.", "avatar."] },
  { folder: "꾸미기", prefixes: ["hero."] }, // hero.base 를 먼저 두었으므로 나머지(모자·소품·룩)
  { folder: "굿즈", prefixes: ["goods."] },
  { folder: "AR", prefixes: ["ar."] },
  { folder: "체험공간", prefixes: ["world.", "floor.", "decor.", "tile."] },
  { folder: "홈화면", prefixes: ["landing.", "recommend.", "event."] },
  { folder: "소리", prefixes: ["sound."] },
];

/** 못 알아본 자리가 가는 곳 — **버리지 않는다.** */
export const ASSET_GROUP_OTHER = "기타";

export function assetGroupOf(key: string): string {
  for (const g of ASSET_GROUPS) if (g.prefixes.some((p) => key.startsWith(p))) return g.folder;
  return ASSET_GROUP_OTHER;
}

export interface GroupedAsset {
  key: string;
  url: string;
}

/**
 * 받을 것들을 폴더별로. **주소가 없는 자리(아직 안 채운 칸·지움 표시)는 뺀다** —
 * 받을 게 없는데 목록에만 세어 두면 「200장 중 40장 실패」로 보인다.
 */
export function groupAssets(
  assets: Record<string, { url?: string; hidden?: boolean } | undefined>,
): { folder: string; items: GroupedAsset[] }[] {
  const bucket = new Map<string, GroupedAsset[]>();
  for (const [key, e] of Object.entries(assets)) {
    if (!e?.url || e.hidden) continue;
    const folder = assetGroupOf(key);
    const list = bucket.get(folder) ?? [];
    list.push({ key, url: e.url });
    bucket.set(folder, list);
  }
  const order = [...ASSET_GROUPS.map((g) => g.folder), ASSET_GROUP_OTHER];
  return [...bucket.entries()]
    .sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]))
    .map(([folder, items]) => ({ folder, items: items.sort((a, b) => (a.key < b.key ? -1 : 1)) }));
}
